/**
 * D&D 5e (SRD 5.1) reference data for the MIXED rule system — Game Data layer.
 *
 * The mixed sheet is D&D-first: six abilities, a d20 for everything, armour
 * class from armour, hit points from a hit die, proficiency bonus from level,
 * spells that are the setting's own bending techniques. That means the numbers
 * have to be the real ones, so they are transcribed rather than approximated:
 *
 *   · equipment tables  → content/dnd5e-weapons.json, extracted from
 *     https://dnd5e.wikidot.com/weapons and https://dnd5e.wikidot.com/armor
 *   · core maths        → SRD 5.1 (CC-BY-4.0): ability modifiers, proficiency
 *     bonus by level, standard array, the 27-point buy, spell save DC, passive
 *     perception, exhaustion, the condition list
 *
 * Nothing in this file is derived from the Avatar homebrew PDFs; those keep
 * their own file (content/bender-progression.ts) because they genuinely differ
 * (the homebrew's benders have their own level table and slot model).
 */
import equipmentJson from '../../content/dnd5e-weapons.json';
import type { Bilingual } from '@/domain/map-generator';

export const SRD_SOURCE = 'https://dnd5e.wikidot.com/ (SRD 5.1, CC-BY-4.0)';

// ------------------------------------------------------------------- abilities

export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'] as const;
export type AbilityId = (typeof ABILITIES)[number];

export const ABILITY_LABELS: Record<AbilityId, Bilingual> = {
  str: { tr: 'Güç', en: 'Strength' },
  dex: { tr: 'Çeviklik', en: 'Dexterity' },
  con: { tr: 'Dayanıklılık', en: 'Constitution' },
  int: { tr: 'Zekâ', en: 'Intelligence' },
  wis: { tr: 'Bilgelik', en: 'Wisdom' },
  cha: { tr: 'Karizma', en: 'Charisma' },
};

/** SRD: modifier = floor((score − 10) / 2). */
export function abilityMod(score: number): number {
  return Math.floor((score - 10) / 2);
}

/** +3 / −1 — the way a sheet writes a modifier. */
export function formatMod(mod: number): string {
  return mod >= 0 ? `+${mod}` : `−${Math.abs(mod)}`;
}

/** SRD proficiency bonus by character level: +2 @1–4 … +6 @17–20. */
export function proficiencyBonus(level: number): number {
  const clamped = Math.max(1, Math.min(20, Math.round(level)));
  return 2 + Math.floor((clamped - 1) / 4);
}

// ---------------------------------------------------------------------- skills

export interface SkillSpec {
  id: string;
  name: Bilingual;
  ability: AbilityId;
}

/** The 18 skills, each with its ability (SRD 5.1). */
export const SKILLS: readonly SkillSpec[] = [
  { id: 'acrobatics', name: { tr: 'Akrobasi', en: 'Acrobatics' }, ability: 'dex' },
  { id: 'animal-handling', name: { tr: 'Hayvan İdaresi', en: 'Animal Handling' }, ability: 'wis' },
  { id: 'arcana', name: { tr: 'Arkana', en: 'Arcana' }, ability: 'int' },
  { id: 'athletics', name: { tr: 'Atletizm', en: 'Athletics' }, ability: 'str' },
  { id: 'deception', name: { tr: 'Aldatma', en: 'Deception' }, ability: 'cha' },
  { id: 'history', name: { tr: 'Tarih', en: 'History' }, ability: 'int' },
  { id: 'insight', name: { tr: 'İçgörü', en: 'Insight' }, ability: 'wis' },
  { id: 'intimidation', name: { tr: 'Gözdağı', en: 'Intimidation' }, ability: 'cha' },
  { id: 'investigation', name: { tr: 'İnceleme', en: 'Investigation' }, ability: 'int' },
  { id: 'medicine', name: { tr: 'Tıp', en: 'Medicine' }, ability: 'wis' },
  { id: 'nature', name: { tr: 'Doğa', en: 'Nature' }, ability: 'int' },
  { id: 'perception', name: { tr: 'Algı', en: 'Perception' }, ability: 'wis' },
  { id: 'performance', name: { tr: 'Performans', en: 'Performance' }, ability: 'cha' },
  { id: 'persuasion', name: { tr: 'İkna', en: 'Persuasion' }, ability: 'cha' },
  { id: 'religion', name: { tr: 'Din', en: 'Religion' }, ability: 'int' },
  { id: 'sleight-of-hand', name: { tr: 'El Çabukluğu', en: 'Sleight of Hand' }, ability: 'dex' },
  { id: 'stealth', name: { tr: 'Gizlilik', en: 'Stealth' }, ability: 'dex' },
  { id: 'survival', name: { tr: 'Hayatta Kalma', en: 'Survival' }, ability: 'wis' },
] as const;

export function skillById(id: string): SkillSpec | undefined {
  return SKILLS.find((skill) => skill.id === id);
}

/** Trained skills offered per nation — the mixed sheet's "background". */
export const SKILL_CHOICE_COUNT = 4;

// ----------------------------------------------------------- ability generation

/** SRD 5.1 standard array. */
export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const;

/** SRD 5.1 point buy: 27 points, scores 8–15 before any bonus. */
export const POINT_BUY_TOTAL = 27;
export const POINT_BUY_COST: Readonly<Record<number, number>> = {
  8: 0,
  9: 1,
  10: 2,
  11: 3,
  12: 4,
  13: 5,
  14: 7,
  15: 9,
};
export const POINT_BUY_MIN = 8;
export const POINT_BUY_MAX = 15;

export function pointBuySpent(scores: readonly number[]): number {
  return scores.reduce((sum, score) => sum + (POINT_BUY_COST[score] ?? 0), 0);
}

export function pointBuyRemaining(scores: readonly number[]): number {
  return POINT_BUY_TOTAL - pointBuySpent(scores);
}

/** Unarmoured AC (SRD): 10 + Dexterity modifier. */
export function unarmoredAc(dexMod: number): number {
  return 10 + dexMod;
}

// -------------------------------------------------------------------- equipment

export type WeaponCategory = 'simple-melee' | 'simple-ranged' | 'martial-melee' | 'martial-ranged';

export interface WeaponRow {
  id: string;
  name: string;
  category: WeaponCategory;
  cost: string;
  costGp: number;
  damage: string;
  damageType: string;
  weight: string;
  properties: string[];
}

export type ArmorCategory = 'light' | 'medium' | 'heavy';

export interface ArmorRow {
  id: string;
  name: string;
  category: ArmorCategory;
  baseAc: number;
  dexBonus: 'full' | 'max2' | 'none';
  maxDex: number | null;
  strength: number | null;
  stealthDisadvantage: boolean;
  weight: string;
  cost: string;
  costGp: number;
}

export interface ShieldRow {
  id: string;
  name: string;
  acBonus: number;
  weight: string;
  cost: string;
  costGp: number;
}

interface EquipmentFile {
  format: string;
  version: number;
  weapons: WeaponRow[];
  armor: ArmorRow[];
  shields: ShieldRow[];
}

const equipment = equipmentJson as unknown as EquipmentFile;

export function getWeapons(): WeaponRow[] {
  return equipment.weapons;
}

export function getArmor(): ArmorRow[] {
  return equipment.armor;
}

export function getShields(): ShieldRow[] {
  return equipment.shields;
}

export function weaponById(id: string): WeaponRow | undefined {
  return equipment.weapons.find((weapon) => weapon.id === id);
}

export function armorById(id: string): ArmorRow | undefined {
  return equipment.armor.find((row) => row.id === id);
}

/**
 * Armour class from armour (SRD): light adds the full Dex modifier, medium caps
 * it at +2, heavy ignores it; a shield adds its bonus on top.
 */
export function armorClassFor(armorId: string | null, shield: boolean, dexMod: number): number {
  const armor = armorId ? armorById(armorId) : undefined;
  const base = armor ? armor.baseAc : unarmoredAc(dexMod);
  const withDex = armor
    ? armor.dexBonus === 'none'
      ? armor.baseAc
      : armor.dexBonus === 'max2'
        ? armor.baseAc + Math.min(2, dexMod)
        : armor.baseAc + dexMod
    : base;
  const shieldBonus = shield ? (equipment.shields[0]?.acBonus ?? 2) : 0;
  return withDex + shieldBonus;
}

// ------------------------------------------------------------------ progression

/** HP on later levels uses the die's fixed average (SRD), never a re-roll. */
export const HIT_DIE_AVERAGE: Readonly<Record<number, number>> = {
  6: 4,
  8: 5,
  10: 6,
  12: 7,
};

/** HP = hit die maximum + Con at 1st level, then average + Con per level. */
export function averageHitPoints(hitDie: number, level: number, conMod: number): number {
  const average = HIT_DIE_AVERAGE[hitDie] ?? Math.floor(hitDie / 2) + 1;
  const first = hitDie + conMod;
  const later = (Math.max(1, level) - 1) * (average + conMod);
  return Math.max(1, first + later);
}

export const ASSUMED_SPEED = 30;

// ------------------------------------------------------------------- conditions

export const CONDITIONS: readonly Bilingual[] = [
  { tr: 'Kör', en: 'Blinded' },
  { tr: 'Büyülenmiş', en: 'Charmed' },
  { tr: 'Sağır', en: 'Deafened' },
  { tr: 'Bitkin', en: 'Exhaustion' },
  { tr: 'Korkmuş', en: 'Frightened' },
  { tr: 'Yakalanmış', en: 'Grappled' },
  { tr: 'Etkisiz', en: 'Incapacitated' },
  { tr: 'Görünmez', en: 'Invisible' },
  { tr: 'Felç', en: 'Paralysed' },
  { tr: 'Taşlaşmış', en: 'Petrified' },
  { tr: 'Zehirlenmiş', en: 'Poisoned' },
  { tr: 'Yerde', en: 'Prone' },
  { tr: 'Kısıtlanmış', en: 'Restrained' },
  { tr: 'Sersemlemiş', en: 'Stunned' },
  { tr: 'Bilinçsiz', en: 'Unconscious' },
] as const;

/** SRD exhaustion, level by level — the homebrew also spends it as a cost. */
export const EXHAUSTION_EFFECTS: readonly Bilingual[] = [
  { tr: '1 — Yetenek kontrollerinde dezavantaj', en: '1 — Disadvantage on ability checks' },
  { tr: '2 — Hız yarıya iner', en: '2 — Speed halved' },
  { tr: '3 — Saldırı ve kurtarma zarlarında dezavantaj', en: '3 — Disadvantage on attack rolls and saving throws' },
  { tr: '4 — Azami HP yarıya iner', en: '4 — Hit point maximum halved' },
  { tr: '5 — Hız 0 olur', en: '5 — Speed reduced to 0' },
  { tr: '6 — Ölüm', en: '6 — Death' },
] as const;

export const DEATH_SAVE_SUCCESSES = 3;
export const DEATH_SAVE_FAILURES = 3;

/** Spell save DC and attack bonus (SRD) — here, for bending techniques. */
export function spellSaveDc(level: number, abilityModifier: number): number {
  return 8 + proficiencyBonus(level) + abilityModifier;
}

export function spellAttackBonus(level: number, abilityModifier: number): number {
  return proficiencyBonus(level) + abilityModifier;
}

export function passivePerception(wisMod: number, level: number, proficient: boolean): number {
  return 10 + wisMod + (proficient ? proficiencyBonus(level) : 0);
}
